// src/components/layouts/renderers/SatisfatorioFrame.tsx
import React, { useEffect, useState } from 'react';
import { AbsoluteFill, Img, Sequence, continueRender, delayRender, useVideoConfig } from 'remotion';
import { AutocutSlot, useAutocutCapture } from '@/remotion/autocutCapture';
import { FillerPlaybackVideo as FillerVideo } from '@/components/preview/FillerPlaybackVideo';
import { useFillerPreviewContext } from '@/components/preview/FillerPreviewContext';
import { getVideoMetadata } from '@remotion/media-utils';
import type { LayoutRendererProps, SatisfatorioConfig } from '@/lib/layouts/types';
import { clampClipRatio } from '@/lib/layouts/clampClipRatio';
import { clampFillerSpeed } from '@/lib/layouts/clampFillerSpeed';
import { computeTitleSeam } from '@/lib/layouts/computeTitleSeam';
import {
  buildFillerSchedule,
  fillerObjectFit,
  fillerObjectPositionY,
  fillerCoverBox,
  computeFillerRegion,
  isImageFillerUrl,
  resolveFillerPlaylist,
  type FillerFit,
  type FillerPosition,
} from '@/lib/layouts/fillerPlaylist';
import { computeReframeTransform } from '@/lib/layers/computeReframeTransform';
import { useLayerFont } from '@/hooks/use-layer-font';

const FALLBACK_FILLER_DURATION_SEC = 8;
const FILLER_PREMOUNT_MAX_FRAMES = 150;

/** Real durations of the playlist videos, resolved once per mount. Uses the
 *  same delayRender pattern as useSourceAspectRatio so the export render
 *  waits for metadata; a failed probe falls back to a fixed rotation. */
function useFillerDurations(urls: string[]): number[] {
  const previews = useFillerPreviewContext();
  const [durations, setDurations] = useState<number[]>(() => urls.map(() => Number.NaN));
  const [handle] = useState(() =>
    urls.length > 1 ? delayRender('Probing filler playlist durations') : null,
  );

  useEffect(() => {
    if (urls.length <= 1) return;
    let cancelled = false;
    Promise.all(
      urls.map((url) =>
        // Images have no duration — they take the fallback slot in the rotation.
        isImageFillerUrl(url)
          ? Promise.resolve(Number.NaN)
          : previews?.[url]?.durationSeconds
            ? Promise.resolve(previews[url].durationSeconds!)
            : getVideoMetadata(previews?.[url]?.url ?? url).then(
              (m) => m.durationInSeconds,
              () => Number.NaN,
            ),
      ),
    ).then((resolved) => {
      if (!cancelled) setDurations(resolved);
      if (handle !== null) continueRender(handle);
    });
    return () => {
      cancelled = true;
    };
    // The playlist is replaced wholesale on edit; joining is a stable identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urls.join('\n'), handle, previews]);

  return durations;
}

/** Natural pixel dimensions of a filler video, probed once (delayRender so the
 *  export waits). Null until resolved or on failure. */
function useVideoDims(url: string): { w: number; h: number } | null {
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);
  const [handle] = useState(() => delayRender('Probing filler dimensions'));
  useEffect(() => {
    let cancelled = false;
    getVideoMetadata(url).then(
      (m) => {
        if (!cancelled) setDims({ w: m.width, h: m.height });
        continueRender(handle);
      },
      () => continueRender(handle),
    );
    return () => {
      cancelled = true;
    };
  }, [url, handle]);
  return dims;
}

/** A cover video anchored top/bottom: scaled to cover the region, centered
 *  horizontally, offset vertically. Falls back to a plain centered cover until
 *  dimensions resolve (or if they can't). */
const AnchoredVideo: React.FC<{
  url: string;
  fillerSpeed: number;
  positionY: FillerPosition;
  regionW: number;
  regionH: number;
  fallbackSrc?: string;
}> = ({ url, fillerSpeed, positionY, regionW, regionH, fallbackSrc }) => {
  const dims = useVideoDims(url);
  const box = dims
    ? fillerCoverBox({ regionW, regionH, mediaW: dims.w, mediaH: dims.h, positionY })
    : null;

  if (!box) {
    return (
      <FillerVideo fallbackSrc={fallbackSrc} src={url} loop muted playbackRate={fillerSpeed} objectFit="cover" style={{ width: '100%', height: '100%' }} />
    );
  }
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      <FillerVideo
        fallbackSrc={fallbackSrc}
        src={url}
        loop
        muted
        playbackRate={fillerSpeed}
        // The box already matches the video aspect (cover-scaled), so 'fill'
        // draws it without any additional crop.
        objectFit="fill"
        style={{ position: 'absolute', width: box.width, height: box.height, left: box.left, top: box.top }}
      />
    </div>
  );
};

/** A single filler slot: a still image or a looping muted video, cropped by
 *  the per-video fit mode and anchored by the per-video position. */
const FillerMedia: React.FC<{
  url: string;
  fillerSpeed: number;
  regionW: number;
  regionH: number;
  fits?: Record<string, FillerFit>;
  positions?: Record<string, FillerPosition>;
}> = ({ url, fillerSpeed, regionW, regionH, fits, positions }) => {
  const previews = useFillerPreviewContext();
  const playbackUrl = previews?.[url]?.url ?? url;
  const objectFit = fillerObjectFit(url, fits);
  const positionY = fillerObjectPositionY(url, positions);

  if (isImageFillerUrl(url)) {
    // Real <img>: CSS object-position anchors the crop directly.
    return (
      <Img
        src={playbackUrl}
        style={{ width: '100%', height: '100%', objectFit, objectPosition: `center ${positionY}` }}
      />
    );
  }

  // Canvas <Video> ignores object-position; only a cover video needs the
  // computed-box treatment to anchor. 'center' and 'contain' keep the simple,
  // byte-identical path.
  if (positionY !== 'center' && objectFit === 'cover') {
    return (
      <AnchoredVideo
        url={playbackUrl}
        fallbackSrc={url}
        fillerSpeed={fillerSpeed}
        positionY={positionY}
        regionW={regionW}
        regionH={regionH}
      />
    );
  }
  return (
    <FillerVideo
      fallbackSrc={url}
      src={playbackUrl}
      loop
      muted
      playbackRate={fillerSpeed}
      objectFit={objectFit}
      style={{ width: '100%', height: '100%' }}
    />
  );
};

/**
 * One filler item (legacy path) or a looping sequence of them. `slot` names it for AutoCut's
 * capture, which draws the playlist natively (`filler:<slot>`).
 */
export const FillerPlaylist: React.FC<{
  urls: string[];
  fillerSpeed: number;
  regionW: number;
  regionH: number;
  fits?: Record<string, FillerFit>;
  positions?: Record<string, FillerPosition>;
  slot?: string;
}> = ({ urls, fillerSpeed, regionW, regionH, fits, positions, slot = 'filler' }) => {
  const { durationInFrames, fps } = useVideoConfig();
  const capture = useAutocutCapture();
  // The capture needs no durations: AutoCut probes the files itself.
  const durations = useFillerDurations(capture ? [] : urls);

  if (capture) {
    return (
      <AutocutSlot
        id={`filler:${slot}`}
        meta={{ kind: 'filler', urls, fits, positions, speed: fillerSpeed }}
      />
    );
  }

  if (urls.length === 1) {
    // Byte-identical to the pre-playlist output for existing projects when the
    // single video keeps the default 'fill' + 'center'.
    return (
      <FillerMedia
        url={urls[0]}
        fillerSpeed={fillerSpeed}
        regionW={regionW}
        regionH={regionH}
        fits={fits}
        positions={positions}
      />
    );
  }

  const schedule = buildFillerSchedule({
    durationsSec: durations,
    fillerSpeed,
    totalDurationInFrames: durationInFrames,
    fps,
    fallbackDurationSec: FALLBACK_FILLER_DURATION_SEC,
  });

  return (
    <>
      {schedule.map((seg, i) => (
        <Sequence
          key={`${seg.index}-${i}`}
          from={seg.from}
          durationInFrames={seg.durationInFrames}
          // Give the next decoder time to initialize before its segment starts.
          // Capping the lead to the previous segment means only one upcoming
          // filler is premounted, even when high playback rates create many
          // short segments.
          premountFor={i === 0
            ? undefined
            : Math.min(FILLER_PREMOUNT_MAX_FRAMES, schedule[i - 1].durationInFrames)}
        >
          <FillerMedia
            url={urls[seg.index]}
            fillerSpeed={fillerSpeed}
            regionW={regionW}
            regionH={regionH}
            fits={fits}
            positions={positions}
          />
        </Sequence>
      ))}
    </>
  );
};

/** Formats a 0–1 fraction as a percentage string, trimming float noise. */
const pct = (fraction: number): string => `${Number((fraction * 100).toFixed(4))}%`;

const TITLE_BASE: React.CSSProperties = {
  fontFamily: 'system-ui,-apple-system,sans-serif',
  fontSize: 52,
  fontWeight: 800,
  lineHeight: 1.2,
  textAlign: 'center',
  whiteSpace: 'pre-wrap',
  maxWidth: '90%',
  padding: '16px 36px',
  borderRadius: 18,
};

/** Visual variants for the division title. 'pill' is the default. */
export function titleFrameStyles(
  frame: SatisfatorioConfig['titleFrame'],
  accentColor: string,
): React.CSSProperties {
  switch (frame) {
    case 'dark':
      return {
        ...TITLE_BASE,
        backgroundColor: 'rgba(17,17,20,0.92)',
        color: '#ffffff',
        border: '2px solid rgba(255,255,255,0.18)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.45)',
      };
    case 'accent':
      return {
        ...TITLE_BASE,
        backgroundColor: accentColor,
        color: '#ffffff',
        fontWeight: 900,
        borderRadius: 12,
        boxShadow: '0 6px 28px rgba(0,0,0,0.5)',
      };
    case 'outline':
      return {
        ...TITLE_BASE,
        backgroundColor: 'transparent',
        color: '#ffffff',
        fontWeight: 900,
        fontSize: 58,
        padding: '8px 24px',
        WebkitTextStroke: '8px #000000',
        // Paint the stroke BEHIND the fill so it doesn't eat into the glyphs.
        paintOrder: 'stroke fill',
        textShadow: '0 4px 16px rgba(0,0,0,0.85)',
      };
    case 'bar':
      return {
        ...TITLE_BASE,
        width: '100%',
        maxWidth: '100%',
        borderRadius: 0,
        backgroundColor: accentColor,
        color: '#ffffff',
        fontWeight: 900,
        padding: '18px 24px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.45)',
      };
    case 'pill':
    default:
      return {
        ...TITLE_BASE,
        backgroundColor: '#ffffff',
        color: '#111111',
        boxShadow: '0 4px 24px rgba(0,0,0,0.45)',
      };
  }
}

/**
 * Splits the canvas between the clip and a muted, looping "satisfying" filler
 * video. The clip half crops to fill (PostFrameComposition passes mediaFit:
 * 'cover' into the media slot); the filler half always covers.
 *
 * The split adapts to the composition aspect ratio: portrait (and square) stacks
 * the regions top/bottom (sized by HEIGHT %); landscape places them side by side
 * (sized by WIDTH %). `clipRatio` is the clip's share of the split axis and
 * `clipPosition` selects which region comes first — top/left vs bottom/right.
 */
export const SatisfatorioFrame: React.FC<LayoutRendererProps<SatisfatorioConfig>> = ({ config, media }) => {
  const { width, height } = useVideoConfig();
  useLayerFont(config.titleFontFamily, config.titleFontUrl);
  const isLandscape = width > height;
  const ratio = clampClipRatio(config.clipRatio);
  const clipFirst = config.clipPosition !== 'bottom';
  const title = config.titleFrame === 'none' ? '' : config.title?.trim() ?? '';
  const fillerSpeed = clampFillerSpeed(config.fillerSpeed);
  // Size the regions along the split axis only; the cross axis stretches to fill.
  const sizeAlongSplit = (splitPct: number): React.CSSProperties =>
    isLandscape ? { width: `${splitPct}%` } : { height: `${splitPct}%` };

  // Optional zoom/pan reframe applied to the media wherever it renders (split
  // cell or floating clipRect cell). Neutral/absent → undefined → `media` is
  // used as-is, so byte-identical output when clipFrame isn't configured.
  const reframe = config.clipFrame ? computeReframeTransform(config.clipFrame) : undefined;
  const mediaWithReframe = reframe ? (
    <div
      data-testid="satisfatorio-clip-reframe"
      style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, transform: reframe.transform }}
    >
      {media}
    </div>
  ) : (
    media
  );

  // The filler occupies only the space NOT taken by the clip. Legacy split:
  // the non-clip share of the split axis. Floating clipRect: the complementary
  // band beside/below the clip cell (never full-bleed behind it). Region pixel
  // size feeds the top/center/bottom anchoring so it aligns within the visible
  // filler strip, not the whole canvas.
  const fillerFrac = (100 - ratio) / 100;
  const fillerRegionRect = config.clipRect
    ? computeFillerRegion(config.clipRect, isLandscape)
    : null;
  const regionW = fillerRegionRect
    ? width * fillerRegionRect.w
    : isLandscape ? width * fillerFrac : width;
  const regionH = fillerRegionRect
    ? height * fillerRegionRect.h
    : isLandscape ? height : height * fillerFrac;

  const playlist = resolveFillerPlaylist(config);
  const fillerContent = playlist.length > 0 ? (
    <FillerPlaylist
      urls={playlist}
      fillerSpeed={fillerSpeed}
      regionW={regionW}
      regionH={regionH}
      fits={config.fillerFits}
      positions={config.fillerPositions}
      slot="filler"
    />
  ) : (
    <div
      data-testid="satisfatorio-filler-placeholder"
      style={{ width: '100%', height: '100%', backgroundColor: '#15161a' }}
    />
  );

  // The title band straddles the clip/filler seam: with a free-floating
  // clipRect this is the cell edge shared with the filler strip (horizontal
  // band for a strip above/below, vertical seam for a strip beside); without
  // clipRect it is the legacy division line. The text stays horizontal in
  // both orientations.
  const seam = computeTitleSeam(config, isLandscape);
  const titleSeamStyle: React.CSSProperties = config.headlineAnchor
    ? {
      left: pct(config.headlineAnchor.x),
      top: pct(config.headlineAnchor.y),
      transform: 'translate(-50%, -50%)',
      width: '90%',
      justifyContent: 'center',
    }
    : seam.axis === 'vertical'
      ? { top: 0, bottom: 0, left: pct(seam.pos), transform: 'translateX(-50%)', alignItems: 'center' }
      : { left: 0, right: 0, top: pct(seam.pos), transform: 'translateY(-50%)' };

  const titleElement = title && (
    <div
      data-testid="satisfatorio-title"
      data-autocut-over=""
      style={{
        position: 'absolute',
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
        ...titleSeamStyle,
      }}
    >
      <span style={{
        ...titleFrameStyles(config.titleFrame, config.titleAccentColor || '#ED1C24'),
        ...(config.titleFontFamily ? { fontFamily: config.titleFontFamily } : {}),
        ...(config.titleFontSize ? { fontSize: config.titleFontSize } : {}),
      }}>
        {title}
      </span>
    </div>
  );

  if (!config.clipRect) {
    const clipRegion = (
      <div
        data-testid="satisfatorio-clip-region"
        style={{ ...sizeAlongSplit(ratio), position: 'relative', overflow: 'hidden', backgroundColor: '#000' }}
      >
        {mediaWithReframe}
      </div>
    );

    const fillerRegion = (
      <div
        data-testid="satisfatorio-filler-region"
        style={{ ...sizeAlongSplit(100 - ratio), position: 'relative', overflow: 'hidden', backgroundColor: '#0b0b0e' }}
      >
        {fillerContent}
      </div>
    );

    return (
      <AbsoluteFill style={{ backgroundColor: '#000', display: 'flex', flexDirection: isLandscape ? 'row' : 'column' }}>
        {clipFirst ? clipRegion : fillerRegion}
        {clipFirst ? fillerRegion : clipRegion}
        {titleElement}
      </AbsoluteFill>
    );
  }

  // clipRect path: the clip is a free-floating cell at the fractional rect, and
  // the filler fills only the complementary band beside/below it (not full-bleed
  // behind the clip).
  const rect = config.clipRect;
  const fr = fillerRegionRect ?? { x: 0, y: 0, w: 1, h: 1 };

  return (
    <AbsoluteFill style={{ backgroundColor: '#000' }}>
      <div
        data-testid="satisfatorio-filler-region"
        style={{
          position: 'absolute',
          left: pct(fr.x),
          top: pct(fr.y),
          width: pct(fr.w),
          height: pct(fr.h),
          overflow: 'hidden',
          backgroundColor: '#0b0b0e',
        }}
      >
        {fillerContent}
      </div>
      <div
        data-testid="satisfatorio-clip-region"
        style={{
          position: 'absolute',
          left: pct(rect.x),
          top: pct(rect.y),
          width: pct(rect.w),
          height: pct(rect.h),
          overflow: 'hidden',
          backgroundColor: '#000',
        }}
      >
        {mediaWithReframe}
      </div>
      {titleElement}
    </AbsoluteFill>
  );
};
